import { applySnapshot, flow, getSnapshot, types } from "mobx-state-tree";
import createTableStore from "./templates/TableStore";
import api from "services/api";
import { MSURL } from "utils/ms";
import { PUBLISH_STATUSES } from "constants/models";
import { isEmptyObject } from "utils";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { TagDictionaryItem } from "./dictionary/TagDictionary";

const pathAnswerOption = "/quiz/api/v1";
export const AnswerOptionModel = types
  .model({
    id: types.number,
    questionID: types.number,
    title: types.string,
    code: types.string,
    icon: types.maybeNull(types.string),
    displayOrder: types.number,
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    tags: types.array(TagDictionaryItem),
  })
  .views(self => ({
    get formData() {
      const uniqueTagsMap = new Map();
      self.tags.map(item => uniqueTagsMap.set(item.id, item));
      const tagArray = Array.from(uniqueTagsMap.values());

      return {
        ...self,
        file: self.icon,
        tag: tagArray.map(({ id, title }) => ({
          id,
          name: title,
        })),
      };
    },
  }));

const AnswerOptionStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(
      `${pathAnswerOption}/answer-option`,
      params,
      undefined,
      MSURL,
    );
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: AnswerOptionModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(AnswerOptionModel),
    isOptionLoading: types.optional(types.boolean, false),
    isOptionManaging: types.optional(types.boolean, false),
    isOptionPublishing: types.optional(types.boolean, false),
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get answerOptions() {
      const { items } = getSnapshot(self);
      return items.map(({ id, questionID, title, code, displayOrder }) => ({
        label: title,
        value: {
          id,
          questionID,
          title,
          code,
          displayOrder,
        },
      }));
    },
  }))
  .actions(self => ({
    updateOption(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchOptionDetails: flow(function*(id: number) {
      self.isOptionLoading = true;

      try {
        const details = yield api.get(
          `${pathAnswerOption}/answer-option/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isOptionLoading = false;
      }
    }),

    createAnswerOption: flow(function*(payload: any) {
      self.isOptionManaging = true;

      try {
        const { id } = yield api.post(
          `${pathAnswerOption}/answer-option`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isOptionManaging = false;
      }
    }),

    editAnswerOptionInfo: flow(function*(payload: any) {
      self.isOptionManaging = true;

      try {
        return yield api.put(
          `${pathAnswerOption}/answer-option/${payload.id}`,
          payload.data,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isOptionManaging = false;
      }
    }),

    editAnswerOptionIcon: flow(function*(payload: any) {
      self.isOptionManaging = true;

      try {
        return yield api.putFormData(
          `${pathAnswerOption}/answer-option/${payload.id}/upload-image`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isOptionManaging = false;
      }
    }),

    changeAnswerOptionStatus: flow(function*(id: number, status: string) {
      self.isOptionPublishing = true;

      try {
        return yield api.post(
          `${pathAnswerOption}/answer-option/${id}/status`,
          {
            status: status,
          },
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isOptionPublishing = false;
      }
    }),

    updateAnswerOptionOrder: flow(function*(payload: any) {
      self.isOptionManaging = true;

      try {
        return yield api.post(
          `${pathAnswerOption}/answer-option/update-order`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isOptionManaging = false;
      }
    }),

    resetOptionsDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default AnswerOptionStore.create();
